import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
} from "react";

import getPlaywrightData from "../util/getPlaywrightData";

export const TenantSlugContext = createContext<string | null>(null);

function getTenantSlug() {
  const playwrightData = getPlaywrightData();
  if (playwrightData?.tenantSlug) {
    // Safari doesn't support subdomains of localhost (https://github.com/microsoft/playwright/discussions/15890)
    // so we use a user agent string to pass the tenant ID to the browser.
    return playwrightData.tenantSlug;
  }

  const hostParts = window.location.host.split(".");
  if (hostParts.length < 3) {
    return null;
  }
  return hostParts[0];
}

export default function TenantSlugProvider({
  children,
}: {
  children: ReactElement;
}): ReactElement | null {
  const tenantSlug = useMemo(getTenantSlug, []);

  useEffect(() => {
    if (!tenantSlug && import.meta.env.STW_DEFAULT_CLIENT_REDIRECT) {
      const newUrl = new URL(window.location.href);
      newUrl.hostname = import.meta.env.STW_DEFAULT_CLIENT_REDIRECT;

      window.location.href = newUrl.href;
    }
  }, [tenantSlug]);

  if (!tenantSlug) {
    return <div>Error: no tenant ID</div>;
  }

  return (
    <TenantSlugContext.Provider value={tenantSlug}>
      {children}
    </TenantSlugContext.Provider>
  );
}

export function useTenantSlug(): string {
  const tenantSlug = useContext(TenantSlugContext);

  if (!tenantSlug) {
    throw new Error("TenantSlugContext not found");
  }

  return tenantSlug;
}
